import React, { useEffect } from "react";
import {
    FaMountain,
    FaRoad,
    FaWater,
    FaCalculator,
    FaFileAlt,
    FaProjectDiagram,
    FaCogs,
    FaTools,
    FaDraftingCompass,
    FaRuler,
    FaLayerGroup,
    FaBuilding,
    FaTruck,
    FaHardHat,
    FaDatabase,
    FaChartLine,
    FaNetworkWired,
    FaRecycle,
    FaLeaf,
    FaTint,
    FaSync,
    FaCheckCircle,
    FaClipboardCheck,
    FaRegulatory,
    FaBalanceScale,
    FaCube,
    FaShapes,
    FaPaintBrush,
    FaServer,
    FaCode,
    FaRobot,
    FaFilter,
    FaCloud,
    FaGlobe,
    FaMapMarkedAlt,
    FaFire
} from "react-icons/fa";

// Placeholder imports - update with actual course feature side images
import sideImage1 from "../../assets/staad-pro/image/FireFlowAnalysis.png";
import sideImage2 from "../../assets/staad-pro/image/StructuralModeling.png";
import sideImage3 from "../../assets/staad-pro/image/concrete-design.png";
import sideImage4 from "../../assets/staad-pro/image/dynamic-analysis.png";
import sideImage5 from "../../assets/staad-pro/image/finite-element.png";
import sideImage6 from "../../assets/staad-pro/image/sideimage6.png";

// Transparent Medini Technologies Logo
import MediniLogo from "../../assets/Image/Medini_logo.png";


const CourseFeatures = ({ courseId }) => {

    const [selectedFeature, setSelectedFeature] = React.useState(null);


    const courseFeaturesData = {
        'openroads-designer': {
            features: [
                {
                    id: "terrain-modeling",
                    title: "Terrain Modeling",
                    description: "Create accurate terrain models using survey data, GPS, LiDAR, and total station measurements for comprehensive site analysis.",
                    icon: <FaMountain />,
                    sideImage: sideImage1,
                    popupTitle: "Terrain Modeling",
                    popupIntro: "Create accurate terrain models with comprehensive tools that help you:",
                    bullets: [
                        { bold: "Import survey data", text: "from various sources including GPS, LiDAR, and total station measurements" },
                        { bold: "Create terrain models", text: "with accurate elevation data and surface analysis capabilities" },
                        { bold: "Analyze topography", text: "with slope analysis, watershed delineation, and cut/fill calculations" },
                    ],
                },
                {
                    id: "corridor-design",
                    title: "Corridor Design",
                    description: "Design complex road alignments with horizontal and vertical geometry, corridor modeling, and template-based cross-sections.",
                    icon: <FaRoad />,
                    sideImage: sideImage2,
                    popupTitle: "Corridor Design",
                    popupIntro: "Design comprehensive road corridors with advanced tools that enable you to:",
                    bullets: [
                        { bold: "Create alignments", text: "with horizontal and vertical geometry for complex road designs" },
                        { bold: "Build corridors", text: "using template-based modeling for consistent cross-sections" },
                        { bold: "Generate cross-sections", text: "automatically with detailed earthwork calculations" },
                    ],
                },
                {
                    id: "drainage-design",
                    title: "Drainage Design",
                    description: "Integrate drainage design including culvert analysis, stormwater management, and utility coordination within transportation projects.",
                    icon: <FaTint />,
                    sideImage: sideImage3,
                    popupTitle: "Drainage Design",
                    popupIntro: "Design comprehensive drainage systems with integrated tools that allow you to:",
                    bullets: [
                        { bold: "Analyze drainage", text: "with culvert analysis and stormwater management capabilities" },
                        { bold: "Coordinate utilities", text: "within transportation projects for comprehensive infrastructure design" },
                        { bold: "Manage stormwater", text: "with detention pond analysis and flood modeling" },
                    ],
                },
                {
                    id: "earthwork-analysis",
                    title: "Earthwork Analysis",
                    description: "Calculate accurate earthwork quantities and generate detailed reports for construction planning and cost estimation.",
                    icon: <FaRuler />,
                    sideImage: sideImage4,
                    popupTitle: "Earthwork Analysis",
                    popupIntro: "Calculate precise earthwork quantities with advanced analysis tools that help you:",
                    bullets: [
                        { bold: "Calculate quantities", text: "with accurate cut/fill calculations for construction planning" },
                        { bold: "Generate reports", text: "with detailed earthwork analysis and cost estimation" },
                        { bold: "Optimize grading", text: "with mass haul diagrams and balance line analysis" },
                    ],
                },
                {
                    id: "annotation-production",
                    title: "Annotation & Production",
                    description: "Create comprehensive construction documentation with automated annotation and sheet production capabilities.",
                    icon: <FaDraftingCompass />,
                    sideImage: sideImage5,
                    popupTitle: "Annotation & Production",
                    popupIntro: "Generate construction documentation efficiently with tools that enable you to:",
                    bullets: [
                        { bold: "Automate annotation", text: "with intelligent labeling and dimensioning capabilities" },
                        { bold: "Produce sheets", text: "with automated sheet generation and plan production" },
                        { bold: "Maintain standards", text: "with customizable annotation styles and CAD standards" },
                    ],
                },
                {
                    id: "bim-integration",
                    title: "BIM Integration",
                    description: "Work with BIM workflows including model coordination, data continuity, and collaboration across project teams.",
                    icon: <FaLayerGroup />,
                    sideImage: sideImage6,
                    popupTitle: "BIM Integration",
                    popupIntro: "Integrate BIM workflows with comprehensive tools that allow you to:",
                    bullets: [
                        { bold: "Maintain data continuity", text: "throughout the project lifecycle with intelligent models" },
                        { bold: "Coordinate models", text: "with clash detection and interdisciplinary coordination" },
                        { bold: "Collaborate effectively", text: "with cloud-based project sharing and team collaboration" },
                    ],
                },
            ]
        },
        'watergems': {
            features: [
                {
                    id: "hydraulic-modeling",
                    title: "Hydraulic Modeling",
                    description: "Build comprehensive water network models including pipes, junctions, pumps, tanks, and control valves for system analysis.",
                    icon: <FaNetworkWired />,
                    sideImage: sideImage1,
                    popupTitle: "Hydraulic Modeling",
                    popupIntro: "Build comprehensive water network models with tools that help you:",
                    bullets: [
                        { bold: "Create networks", text: "with pipes, junctions, pumps, tanks, and control valves" },
                        { bold: "Model systems", text: "from source to tap with comprehensive network analysis" },
                        { bold: "Validate designs", text: "with hydraulic analysis and pressure management" },
                    ],
                },
                {
                    id: "demand-analysis",
                    title: "Demand Analysis",
                    description: "Allocate water demand patterns for residential, commercial, and industrial areas with proper pipe sizing methodologies.",
                    icon: <FaChartLine />,
                    sideImage: sideImage2,
                    popupTitle: "Demand Analysis",
                    popupIntro: "Analyze water demand patterns with comprehensive tools that enable you to:",
                    bullets: [
                        { bold: "Allocate demand", text: "for residential, commercial, and industrial areas" },
                        { bold: "Size pipes", text: "with proper methodologies for optimal system performance" },
                        { bold: "Model patterns", text: "with time-varying demand for accurate system analysis" },
                    ],
                },
                {
                    id: "eps-simulation",
                    title: "Extended Period Simulation",
                    description: "Analyze system behavior over time with EPS for operational scenarios, pump scheduling, and tank level management.",
                    icon: <FaSync />,
                    sideImage: sideImage3,
                    popupTitle: "Extended Period Simulation",
                    popupIntro: "Analyze system behavior over time with EPS tools that allow you to:",
                    bullets: [
                        { bold: "Simulate operations", text: "over extended periods with time-varying conditions" },
                        { bold: "Schedule pumps", text: "for optimal energy efficiency and system performance" },
                        { bold: "Manage tanks", text: "with level analysis and refill cycle optimization" },
                    ],
                },
                {
                    id: "fire-flow-analysis",
                    title: "Fire Flow Analysis",
                    description: "Evaluate system capacity for emergency fire flow requirements and ensure adequate pressure for fire protection.",
                    icon: <FaFire />,
                    sideImage: sideImage4,
                    popupTitle: "Fire Flow Analysis",
                    popupIntro: "Evaluate fire protection capabilities with analysis tools that help you:",
                    bullets: [
                        { bold: "Assess capacity", text: "for emergency fire flow requirements" },
                        { bold: "Ensure pressure", text: "meets fire protection standards and regulations" },
                        { bold: "Identify deficiencies", text: "in system capacity for emergency scenarios" },
                    ],
                },
                {
                    id: "model-calibration",
                    title: "Model Calibration",
                    description: "Calibrate models with field data to ensure accuracy and reliability for system planning and operation.",
                    icon: <FaCheckCircle />,
                    sideImage: sideImage5,
                    popupTitle: "Model Calibration",
                    popupIntro: "Calibrate models with field data using tools that enable you to:",
                    bullets: [
                        { bold: "Compare results", text: "with field data for model validation" },
                        { bold: "Adjust parameters", text: "to improve model accuracy and reliability" },
                        { bold: "Optimize performance", text: "with sensitivity analysis and parameter tuning" },
                    ],
                },
                {
                    id: "water-quality",
                    title: "Water Quality Analysis",
                    description: "Analyze water quality parameters including age, chlorine decay, and contaminant transport throughout the system.",
                    icon: <FaLeaf />,
                    sideImage: sideImage6,
                    popupTitle: "Water Quality Analysis",
                    popupIntro: "Analyze water quality with comprehensive tools that allow you to:",
                    bullets: [
                        { bold: "Track water age", text: "throughout the distribution system" },
                        { bold: "Model chlorine decay", text: "for disinfection planning and regulatory compliance" },
                        { bold: "Simulate contaminants", text: "transport for emergency response planning" },
                    ],
                },
            ]
        },
        'staad-pro': {
            features: [
                {
                    id: "structural-modeling",
                    title: "Structural Modeling",
                    description: "Create 3D structural models with comprehensive geometry, sections, materials, and load definitions for analysis.",
                    icon: <FaCube />,
                    sideImage: sideImage1,
                    popupTitle: "Structural Modeling",
                    popupIntro: "Create comprehensive structural models with tools that help you:",
                    bullets: [
                        { bold: "Build geometry", text: "with 3D modeling capabilities for complex structures" },
                        { bold: "Define sections", text: "with extensive library of standard and custom sections" },
                        { bold: "Specify materials", text: "with comprehensive material properties and design codes" },
                    ],
                },
                {
                    id: "load-analysis",
                    title: "Load Analysis",
                    description: "Apply various load combinations including dead, live, wind, seismic, and thermal loads for comprehensive structural analysis.",
                    icon: <FaBalanceScale />,
                    sideImage: sideImage2,
                    popupTitle: "Load Analysis",
                    popupIntro: "Apply comprehensive loads with tools that enable you to:",
                    bullets: [
                        { bold: "Define loads", text: "including dead, live, wind, seismic, and thermal loads" },
                        { bold: "Create combinations", text: "with automatic load combination generation" },
                        { bold: "Apply codes", text: "according to international design standards" },
                    ],
                },
                {
                    id: "steel-design",
                    title: "Steel Design",
                    description: "Design steel structures according to international codes including AISC, Eurocode, and IS specifications.",
                    icon: <FaBuilding />,
                    sideImage: sideImage3,
                    popupTitle: "Steel Design",
                    popupIntro: "Design steel structures with comprehensive tools that allow you to:",
                    bullets: [
                        { bold: "Design members", text: "according to AISC, Eurocode, and IS specifications" },
                        { bold: "Check capacity", text: "with detailed stress and deflection analysis" },
                        { bold: "Optimize sections", text: "for efficient and economical design" },
                    ],
                },
                {
                    id: "concrete-design",
                    title: "Concrete Design",
                    description: "Design reinforced concrete elements with beam, column, slab, and foundation design capabilities.",
                    icon: <FaHardHat />,
                    sideImage: sideImage4,
                    popupTitle: "Concrete Design",
                    popupIntro: "Design concrete elements with comprehensive tools that help you:",
                    bullets: [
                        { bold: "Design beams", text: "with flexural and shear reinforcement design" },
                        { bold: "Design columns", text: "with axial load and moment capacity analysis" },
                        { bold: "Design foundations", text: "with bearing capacity and settlement analysis" },
                    ],
                },
                {
                    id: "dynamic-analysis",
                    title: "Dynamic Analysis",
                    description: "Perform dynamic analysis including response spectrum, time history, and seismic analysis for earthquake-resistant design.",
                    icon: <FaChartLine />,
                    sideImage: sideImage5,
                    popupTitle: "Dynamic Analysis",
                    popupIntro: "Perform dynamic analysis with advanced tools that enable you to:",
                    bullets: [
                        { bold: "Analyze response", text: "with response spectrum and time history analysis" },
                        { bold: "Design for seismic", text: "with comprehensive earthquake-resistant design capabilities" },
                        { bold: "Evaluate performance", text: "with modal analysis and frequency response" },
                    ],
                },
                {
                    id: "finite-element",
                    title: "Finite Element Analysis",
                    description: "Perform detailed finite element analysis for complex structures with plate, shell, and solid elements.",
                    icon: <FaShapes />,
                    sideImage: sideImage6,
                    popupTitle: "Finite Element Analysis",
                    popupIntro: "Perform FEA with comprehensive tools that allow you to:",
                    bullets: [
                        { bold: "Model complex structures", text: "with plate, shell, and solid elements" },
                        { bold: "Analyze stress", text: "with detailed stress and strain distribution" },
                        { bold: "Optimize design", text: "with advanced finite element analysis capabilities" },
                    ],
                },
            ]
        },
        'microstation': {
            features: [
                {
                    id: "2d-drafting",
                    title: "2D Drafting",
                    description: "Create precise 2D drawings with comprehensive drafting tools, dimensioning, and annotation capabilities.",
                    icon: <FaDraftingCompass />,
                    sideImage: sideImage1,
                    popupTitle: "2D Drafting",
                    popupIntro: "Create precise 2D drawings with tools that help you:",
                    bullets: [
                        { bold: "Draft accurately", text: "with comprehensive drawing and editing tools" },
                        { bold: "Dimension properly", text: "with automated dimensioning and annotation" },
                        { bold: "Maintain standards", text: "with customizable CAD standards and templates" },
                    ],
                },
                {
                    id: "3d-modeling",
                    title: "3D Modeling",
                    description: "Build detailed 3D models with solid modeling, surface modeling, and parametric design tools.",
                    icon: <FaCube />,
                    sideImage: sideImage2,
                    popupTitle: "3D Modeling",
                    popupIntro: "Build detailed 3D models with tools that enable you to:",
                    bullets: [
                        { bold: "Create solids", text: "with comprehensive solid modeling capabilities" },
                        { bold: "Model surfaces", text: "with advanced surface modeling tools" },
                        { bold: "Design parametrically", text: "with constraint-based parametric design" },
                    ],
                },
                {
                    id: "bim-integration",
                    title: "BIM Integration",
                    description: "Work with BIM workflows including IFC import/export, model coordination, and collaboration tools.",
                    icon: <FaGlobe />,
                    sideImage: sideImage3,
                    popupTitle: "BIM Integration",
                    popupIntro: "Integrate BIM workflows with tools that allow you to:",
                    bullets: [
                        { bold: "Import/export IFC", text: "for seamless BIM data exchange" },
                        { bold: "Coordinate models", text: "with clash detection and interdisciplinary coordination" },
                        { bold: "Collaborate effectively", text: "with cloud-based project sharing" },
                    ],
                },
                {
                    id: "visualization",
                    title: "Visualization",
                    description: "Create stunning renderings and visualizations with integrated rendering tools and material libraries.",
                    icon: <FaPaintBrush />,
                    sideImage: sideImage4,
                    popupTitle: "Visualization",
                    popupIntro: "Create stunning visualizations with tools that help you:",
                    bullets: [
                        { bold: "Render realistically", text: "with integrated rendering engines" },
                        { bold: "Apply materials", text: "with comprehensive material libraries" },
                        { bold: "Create animations", text: "with walkthrough and animation capabilities" },
                    ],
                },
                {
                    id: "data-management",
                    title: "Data Management",
                    description: "Manage project data with comprehensive tools for file management, version control, and collaboration.",
                    icon: <FaDatabase />,
                    sideImage: sideImage5,
                    popupTitle: "Data Management",
                    popupIntro: "Manage project data with tools that enable you to:",
                    bullets: [
                        { bold: "Organize files", text: "with comprehensive project management tools" },
                        { bold: "Control versions", text: "with integrated version control and history" },
                        { bold: "Collaborate effectively", text: "with team collaboration and sharing" },
                    ],
                },
                {
                    id: "automation",
                    title: "Automation",
                    description: "Automate workflows with scripting, macros, and custom applications for increased productivity.",
                    icon: <FaRobot />,
                    sideImage: sideImage6,
                    popupTitle: "Automation",
                    popupIntro: "Automate workflows with tools that allow you to:",
                    bullets: [
                        { bold: "Create scripts", text: "with VBA and Python automation capabilities" },
                        { bold: "Build macros", text: "for repetitive task automation" },
                        { bold: "Develop applications", text: "with custom API and plugin development" },
                    ],
                },
            ]
        },
        'sewergems': {
            features: [
                {
                    id: "sanitary-modeling",
                    title: "Sanitary Modeling",
                    description: "Model sanitary sewer systems with comprehensive network analysis and capacity evaluation.",
                    icon: <FaNetworkWired />,
                    sideImage: sideImage1,
                    popupTitle: "Sanitary Modeling",
                    popupIntro: "Model sanitary sewer systems with tools that help you:",
                    bullets: [
                        { bold: "Build networks", text: "with comprehensive sanitary sewer modeling" },
                        { bold: "Analyze capacity", text: "with detailed hydraulic analysis" },
                        { bold: "Evaluate performance", text: "with system capacity and flow analysis" },
                    ],
                },
                {
                    id: "stormwater-analysis",
                    title: "Stormwater Analysis",
                    description: "Analyze stormwater systems including detention ponds, channels, and drainage networks.",
                    icon: <FaCloud />,
                    sideImage: sideImage2,
                    popupTitle: "Stormwater Analysis",
                    popupIntro: "Analyze stormwater systems with tools that enable you to:",
                    bullets: [
                        { bold: "Model stormwater", text: "with comprehensive drainage network analysis" },
                        { bold: "Design ponds", text: "with detention pond analysis and sizing" },
                        { bold: "Analyze channels", text: "with open channel flow analysis" },
                    ],
                },
                {
                    id: "combined-systems",
                    title: "Combined Systems",
                    description: "Design and analyze combined sewer systems with proper overflow and diversion strategies.",
                    icon: <FaRecycle />,
                    sideImage: sideImage3,
                    popupTitle: "Combined Systems",
                    popupIntro: "Design combined systems with tools that allow you to:",
                    bullets: [
                        { bold: "Model combined sewers", text: "with comprehensive system analysis" },
                        { bold: "Design overflows", text: "with CSO analysis and diversion strategies" },
                        { bold: "Optimize operations", text: "with system performance evaluation" },
                    ],
                },
                {
                    id: "system-optimization",
                    title: "System Optimization",
                    description: "Optimize sewer system performance with pump station analysis and operational scenario testing.",
                    icon: <FaCogs />,
                    sideImage: sideImage4,
                    popupTitle: "System Optimization",
                    popupIntro: "Optimize system performance with tools that help you:",
                    bullets: [
                        { bold: "Analyze pumps", text: "with pump station analysis and optimization" },
                        { bold: "Test scenarios", text: "with operational scenario evaluation" },
                        { bold: "Improve efficiency", text: "with system performance optimization" },
                    ],
                },
                {
                    id: "inflow-infiltration",
                    title: "Inflow & Infiltration",
                    description: "Analyze and model inflow and infiltration for accurate system capacity planning and rehabilitation.",
                    icon: <FaFilter />,
                    sideImage: sideImage5,
                    popupTitle: "Inflow & Infiltration",
                    popupIntro: "Analyze I&I with tools that enable you to:",
                    bullets: [
                        { bold: "Model inflow", text: "with comprehensive I&I analysis" },
                        { bold: "Identify sources", text: "with detailed system evaluation" },
                        { bold: "Plan rehabilitation", text: "with capacity improvement strategies" },
                    ],
                },
                {
                    id: "regulatory-compliance",
                    title: "Regulatory Compliance",
                    description: "Ensure compliance with environmental regulations and standards for sewer system design and operation.",
                    icon: <FaClipboardCheck />,
                    sideImage: sideImage6,
                    popupTitle: "Regulatory Compliance",
                    popupIntro: "Ensure compliance with tools that allow you to:",
                    bullets: [
                        { bold: "Meet regulations", text: "with comprehensive regulatory analysis" },
                        { bold: "Document compliance", text: "with detailed reporting capabilities" },
                        { bold: "Plan improvements", text: "with regulatory-driven system upgrades" },
                    ],
                },
            ]
        }
    };

    const features = courseFeaturesData[courseId]?.features || [];

    /*
    Prevent background page scrolling
    when popup is open
    */

    useEffect(() => {

        if (selectedFeature) {

            document.body.style.overflow = "hidden";

        } else {

            document.body.style.overflow = "";

        }


        return () => {

            document.body.style.overflow = "";

        };

    }, [selectedFeature]);


    /*
    Close popup with ESC key
    */

    useEffect(() => {

        const handleKeyDown = (event) => {

            if (event.key === "Escape") {

                setSelectedFeature(null);

            }

        };


        window.addEventListener(
            "keydown",
            handleKeyDown
        );


        return () => {

            window.removeEventListener(
                "keydown",
                handleKeyDown
            );

        };

    }, []);

    if (features.length === 0) {
        return null;
    }


    return (

        <section className="course-features-section">

            <div className="course-features-container">


                <h2 className="course-features-heading">

                    Features for your creativity

                </h2>


                <div className="course-features-grid">


                    {features.map((feature) => (

                        <div

                            className="course-feature-card"

                            key={feature.id}

                            onClick={() =>
                                setSelectedFeature(feature)
                            }

                        >


                            <div className="course-feature-icon-wrapper">

                                <div className="course-feature-icon">
                                    {feature.icon}
                                </div>

                            </div>


                            <div className="course-feature-content">


                                <h3>

                                    {feature.title}

                                </h3>


                                <p>

                                    {feature.description}

                                </p>


                            </div>


                        </div>

                    ))}


                </div>


            </div>


            {/* =====================================================
                FULL SCREEN FEATURE POPUP
            ====================================================== */}


            {selectedFeature && (

                <div className="course-feature-popup">


                    {/* =================================================
                        POPUP HEADER / RIBBON
                    ================================================= */}


                    <div className="course-feature-popup-header">


                        {/* LEFT — MEDINI LOGO */}

                        <div className="course-feature-popup-logo-wrapper">

                            <img

                                src={MediniLogo}

                                alt="Medini Technologies"

                                className="course-feature-popup-logo"

                            />

                        </div>


                        {/* RIGHT — CLOSE BUTTON */}

                        <button

                            className="course-feature-popup-close"

                            onClick={() =>
                                setSelectedFeature(null)
                            }

                            aria-label="Close feature details"

                        >

                            ×

                        </button>


                    </div>


                    {/* =================================================
                        POPUP MAIN CONTENT
                    ================================================= */}


                    <div className="course-feature-popup-content">


                        {/* LEFT — CONTENT */}

                        <div className="course-feature-popup-text">


                            <h2>

                                {selectedFeature.popupTitle
                                    || selectedFeature.title}

                            </h2>


                            <p className="course-feature-popup-description">

                                {selectedFeature.popupIntro}

                            </p>


                            {selectedFeature.bullets && (

                                <ul className="course-feature-popup-bullets">

                                    {selectedFeature.bullets.map(
                                        (bullet, index) => (

                                            <li key={index}>

                                                <strong>

                                                    {bullet.bold}

                                                </strong>{" "}

                                                {bullet.text}

                                            </li>

                                        )
                                    )}

                                </ul>

                            )}


                        </div>


                        {/* RIGHT — IMAGE */}

                        <div className="course-feature-popup-image-wrapper">

                            <img

                                src={selectedFeature.sideImage}

                                alt={selectedFeature.title}

                                className="course-feature-popup-image"

                            />

                        </div>


                    </div>


                </div>

            )}


            <style>{`

                /* =================================================
                   MAIN FEATURES SECTION
                ================================================= */


                .course-features-section {

                    width: 100%;

                    background: #ffffff;

                    padding: 70px 20px 80px;

                    box-sizing: border-box;

                    font-family:
                        'Plus Jakarta Sans',
                        sans-serif;

                }


                .course-features-container {

                    width: 100%;

                    max-width: 1400px;

                    margin: 0 auto;

                }


                .course-features-heading {

                    margin:
                        0 0 55px;

                    color: #000000;

                    font-size: 42px;

                    font-weight: 600;

                    line-height: 1.2;

                    letter-spacing: -1.5px;

                }


                /* =================================================
                   FEATURES GRID
                ================================================= */


                .course-features-grid {

                    display: grid;

                    grid-template-columns:
                        repeat(4, 1fr);

                    gap: 26px;

                    align-items: stretch;

                }


                /* =================================================
                   FEATURE CARD
                ================================================= */


                .course-feature-card {

                    min-height: 310px;

                    padding:
                        28px
                        28px
                        30px;

                    box-sizing: border-box;

                    background: #eaf6fc;

                    border:
                        1px solid
                        #d5dce0;

                    border-radius: 16px;

                    display: flex;

                    flex-direction: column;

                    justify-content: space-between;

                    cursor: pointer;

                    transition:
                        transform 0.25s ease,
                        box-shadow 0.25s ease,
                        border-color 0.25s ease;

                }


                .course-feature-card:hover {

                    transform:
                        translateY(-5px);

                    box-shadow:
                        0 14px 32px
                        rgba(
                            0,
                            0,
                            0,
                            0.09
                        );

                    border-color:
                        #b9cbd2;

                }


                /* =================================================
                   FEATURE ICON
                ================================================= */


                .course-feature-icon-wrapper {

                    width: 75px;

                    height: 75px;

                    display: flex;

                    align-items:
                        flex-start;

                    justify-content:
                        flex-start;

                    margin-bottom:
                        22px;

                }


                .course-feature-icon {

                    width: 100%;

                    height: 100%;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    font-size: 48px;

                    color: #356575;

                }


                /* =================================================
                   FEATURE CONTENT
                ================================================= */


                .course-feature-content {

                    margin-top: auto;

                }


                .course-feature-content h3 {

                    margin:
                        0 0 10px;

                    color:
                        #000000;

                    font-size:
                        20px;

                    font-weight:
                        600;

                    line-height:
                        1.3;

                }


                .course-feature-content p {

                    margin:
                        0;

                    max-width:
                        100%;

                    color:
                        #111111;

                    font-size:
                        14px;

                    font-weight:
                        400;

                    line-height:
                        1.55;

                    text-align: left;

                }


                /* =================================================
                   FULL SCREEN POPUP BACKGROUND
                ================================================= */


                .course-feature-popup {

                    position: fixed;

                    top: 0;

                    right: 0;

                    bottom: 0;

                    left: 0;

                    width: 100vw;

                    height: 100vh;

                    background: #ffffff;

                    z-index: 999999;

                    display: flex;

                    flex-direction: column;

                    box-sizing: border-box;

                    overflow: hidden;

                }


                /* =================================================
                   POPUP RIBBON / HEADER

                   RGB 53, 101, 117
                   #356575
                ================================================= */


                .course-feature-popup-header {

                    width:
                        100%;

                    height:
                        76px;

                    min-height:
                        76px;

                    background:
                        rgb(
                            53,
                            101,
                            117
                        );

                    display:
                        flex;

                    align-items:
                        center;

                    justify-content:
                        space-between;

                    padding:
                        0
                        42px;

                    box-sizing:
                        border-box;

                    position:
                        relative;

                    z-index:
                        10;

                }


                /* =================================================
                   MEDINI LOGO — LEFT
                ================================================= */


                .course-feature-popup-logo-wrapper {

                    display:
                        flex;

                    align-items:
                        center;

                    justify-content:
                        flex-start;

                    height:
                        100%;

                }


                .course-feature-popup-logo {

                    display:
                        block;

                    width:
                        175px;

                    max-width:
                        175px;

                    height:
                        auto;

                    max-height:
                        52px;

                    object-fit:
                        contain;

                }


                /* =================================================
                   CLOSE BUTTON — RIGHT
                ================================================= */


                .course-feature-popup-close {

                    width:
                        42px;

                    height:
                        42px;

                    display:
                        flex;

                    align-items:
                        center;

                    justify-content:
                        center;

                    border:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.45
                        );

                    border-radius:
                        50%;

                    background:
                        rgba(
                            255,
                            255,
                            255,
                            0.08
                        );

                    color:
                        #ffffff;

                    font-size:
                        30px;

                    font-weight:
                        300;

                    line-height:
                        1;

                    cursor:
                        pointer;

                    padding:
                        0;

                    transition:
                        all 0.2s ease;

                }


                .course-feature-popup-close:hover {

                    background:
                        #ffffff;

                    color:
                        rgb(
                            53,
                            101,
                            117
                        );

                    transform:
                        scale(
                            1.06
                        );

                }


                /* =================================================
                   POPUP MAIN CONTENT

                   CONTENT LEFT
                   IMAGE RIGHT

                   BOTH DIRECTLY UNDER RIBBON
                ================================================= */


                .course-feature-popup-content {

                    width:
                        100%;

                    flex:
                        1;

                    display:
                        grid;

                    grid-template-columns:
                        0.85fr
                        1.15fr;

                    gap:
                        60px;

                    align-items:
                        center;

                    padding:
                        45px
                        7vw
                        50px;

                    box-sizing:
                        border-box;

                    overflow-y:
                        auto;

                    overflow-x:
                        hidden;

                }


                /* =================================================
                   LEFT CONTENT
                ================================================= */


                .course-feature-popup-text {

                    display:
                        flex;

                    flex-direction:
                        column;

                    justify-content:
                        center;

                    max-width:
                        600px;

                }


                .course-feature-popup-text h2 {

                    margin:
                        0 0 18px;

                    color:
                        #111111;

                    font-size:
                        38px;

                    font-weight:
                        600;

                    line-height:
                        1.2;

                    letter-spacing:
                        -1px;

                }


                /* =================================================
                   POPUP DESCRIPTION
                ================================================= */


                .course-feature-popup-description {

                    width:
                        100%;

                    margin:
                        0;

                    color:
                        #555555;

                    font-size:
                        17px;

                    font-weight:
                        400;

                    line-height:
                        1.7;

                    text-align: left;

                }


                .course-feature-popup-bullets {

                    margin: 20px 0 0;

                    padding: 0;

                    list-style: disc;

                    padding-left: 20px;

                    display: flex;

                    flex-direction: column;

                    gap: 15px;

                }


                .course-feature-popup-bullets li {

                    color: #555555;

                    font-size: 16px;

                    font-weight: 400;

                    line-height: 1.5;

                    text-align: left;

                }


                .course-feature-popup-bullets li strong {

                    font-weight: 600;

                }


                /* =================================================
                   RIGHT IMAGE
                ================================================= */


                .course-feature-popup-image-wrapper {

                    width:
                        100%;

                    height:
                        auto;

                    overflow:
                        hidden;

                    border-radius:
                        12px;

                    background:
                        #000000;

                    box-shadow:
                        0 15px 40px
                        rgba(
                            0,
                            0,
                            0,
                            0.14
                        );

                }


                .course-feature-popup-image {

                    display:
                        block;

                    width:
                        100%;

                    height:
                        auto;

                    max-height:
                        68vh;

                    object-fit:
                        cover;

                    background:
                        #000000;

                }


                /* =================================================
                   LARGE TABLET
                ================================================= */


                @media (max-width: 1200px) {


                    .course-features-grid {

                        grid-template-columns:
                            repeat(4, 1fr);

                        gap:
                            20px;

                    }


                    .course-feature-card {

                        min-height:
                            300px;

                        padding:
                            24px;

                    }


                    .course-feature-content h3 {

                        font-size:
                            18px;

                    }


                    .course-feature-content p {

                        font-size:
                            13px;

                    }


                    .course-feature-popup-content {

                        grid-template-columns:
                            1fr
                            1.2fr;

                        gap:
                            40px;

                        padding:
                            40px
                            5vw;

                    }

                }


                /* =================================================
                   TABLET

                   2 CARDS PER ROW
                ================================================= */


                @media (max-width: 950px) {


                    .course-features-grid {

                        grid-template-columns:
                            repeat(2, 1fr);

                    }


                    .course-features-heading {

                        font-size:
                            38px;

                    }


                    .course-feature-popup-header {

                        height:
                            70px;

                        min-height:
                            70px;

                        padding:
                            0
                            28px;

                    }


                    .course-feature-popup-content {

                        grid-template-columns:
                            1fr;

                        gap:
                            30px;

                        align-items:
                            start;

                        padding:
                            35px
                            40px
                            50px;

                    }


                    .course-feature-popup-text {

                        max-width:
                            100%;

                    }


                    .course-feature-popup-image-wrapper {

                        width:
                            100%;

                    }


                    .course-feature-popup-image {

                        max-height:
                            55vh;

                    }

                }


                /* =================================================
                   MOBILE
                ================================================= */


                @media (max-width: 600px) {


                    .course-features-section {

                        padding:
                            50px
                            18px
                            60px;

                    }


                    .course-features-heading {

                        font-size:
                            30px;

                        margin-bottom:
                            38px;

                    }


                    .course-features-grid {

                        grid-template-columns:
                            1fr;

                        gap:
                            20px;

                    }


                    .course-feature-card {

                        min-height:
                            280px;

                        padding:
                            25px;

                    }


                    .course-feature-icon-wrapper {

                        width:
                            65px;

                        height:
                            65px;

                        margin-bottom:
                            18px;

                    }


                    .course-feature-content h3 {

                        font-size:
                            20px;

                    }


                    .course-feature-content p {

                        font-size:
                            14px;

                    }


                    /* POPUP HEADER */


                    .course-feature-popup-header {

                        height:
                            64px;

                        min-height:
                            64px;

                        padding:
                            0
                            18px;

                    }


                    .course-feature-popup-logo {

                        width:
                            145px;

                        max-width:
                            145px;

                        max-height:
                            44px;

                    }


                    .course-feature-popup-close {

                        width:
                            38px;

                        height:
                            38px;

                        font-size:
                            27px;

                    }


                    /* POPUP CONTENT */


                    .course-feature-popup-content {

                        display:
                            flex;

                        flex-direction:
                            column;

                        gap:
                            25px;

                        padding:
                            30px
                            20px
                            40px;

                        overflow-y:
                            auto;

                    }


                    .course-feature-popup-text {

                        width:
                            100%;

                    }


                    .course-feature-popup-text h2 {

                        font-size:
                            30px;

                        margin-bottom:
                            14px;

                    }


                    .course-feature-popup-description {

                        font-size:
                            15px;

                        line-height:
                            1.6;

                    }


                    .course-feature-popup-image-wrapper {

                        width:
                            100%;

                        border-radius:
                            10px;

                    }


                    .course-feature-popup-image {

                        width:
                            100%;

                        max-height:
                            none;

                    }

                }

            `}</style>

        </section>

    );

};
export default CourseFeatures;
